import { Carousel } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

// internal hooks
import { useVerifyUnique } from "../../hooks/useVerifyUnique";
import { useEscape } from "../../hooks/useEscape";

// styles and images
import "./CreateProject.css";

// components
import FscAreaDropdown from "./FscAreaDropdown";
import JobsDropdown from "./JobsDropdown";
import Close from "./Close";
import DropDown from "../../formComponents/DropDown";
import AssetLoadsCarousel from "./AssetLoadsCarousel";

// store
import { userStore, dbDataStore, projectStore, messageAndErrorStore } from "../../store/Store";
import FormButton from "../../formComponents/FormButton";

export default function CreateProject({
  isTriggered,
  setIsTriggered,
  dropDowns,
  projectId,
}) {
  // global state
  const JwtToken = userStore((userDataStore) => userDataStore.JwtToken);
  const currentUser = userStore((userDataStore) => userDataStore.currentUser);
  const currentCompany = userStore(
    (userDataStore) => userDataStore.currentCompany,
  );
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const projectData = dbDataStore((dbData) => dbData.projectData);
  const setProjectData = dbDataStore((dbData) => dbData.setProjectData);
  const setProjectLoads = projectStore((dbData) => dbData.setProjectLoads);
  const setProjectAssetLoads = projectStore(
    (dbData) => dbData.setProjectAssetLoads,
  );
  const setProjectTotals = projectStore((dbData) => dbData.setProjectTotals);
  const setCurrentProject = projectStore((dbData) => dbData.setCurrentProject);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // state
  const [pending, setPending] = useState(false);
  const [index, setIndex] = useState(0);
  const [formError, setFormError] = useState(null);
  const [errIdx, setErrIdx] = useState(null);
  const [oldId, setOldId] = useState("");
  const [isFixedCost, setIsFixedCost] = useState(false);
  const [isFixedRev, setIsFixedRev] = useState(false);
  const [useTieredRates, setUseTieredRates] = useState(false);
  const [useRevFscMileage, setUseRevFscMileage] = useState(false);
  const [useCostFscMileage, setUseCostFscMileage] = useState(false);
  const [rsOptions, setRsOptions] = useState([]);
  const [tierOptions, setTierOptions] = useState([]);
  const [newJob, setNewJob] = useState({
    id: 0,
    job_name: "",
    pc_job_id: "",
    customer_id: 0,
    vendor_id: 0,
    owner_company_id: currentCompany.id,
    fsc_area_id: null,
    fsc_area_name: null,
    customer_fsc_id: null,
    vendor_fsc_id: null,
    customer_rates_id: 0,
    vendor_rates_id: 0,
    customer_equipment_rate_id: 0,
    customer_empty_boxes_rate_id: 0,
    customer_full_boxes_rate_id: 0,
    vendor_equipment_rate_id: 0,
    vendor_empty_boxes_rate_id: 0,
    vendor_full_boxes_rate_id: 0,
    apply_fsc_to_asset_loads: false,
    fixed_mob_rev: 0,
    fixed_mob_cost: 0,
    created_by_id: 0,
    updated_by_id: 0,
    use_propconnect_rates: false,
    rate_sheet_tier_id: 0,
    rev_fsc_mileage_rate: 0,
    cost_fsc_mileage_rate: 0,
    fsc_offset: 0,
  });
  const [usePcRates, setUsePcRates] = useState(newJob.use_propconnect_rates);
  const navigate = useNavigate();
  const { exists } = useVerifyUnique(
    projectData,
    "pc_job_id",
    newJob.pc_job_id,
    oldId,
  );
  const { fscPending } = useOutletContext();
  
  const fscOffsetOptions = [
    {value: 0, label: "Monday"},
    {value: 1, label: "Tuesday"},
    {value: 2, label: "Wednesday"},
    {value: 3, label: "Thursday"},
    {value: 4, label: "Friday"},
    {value: 5, label: "Saturday"},
    {value: 6, label: "Sunday"},
  ]

  useEffect(() => {
    if (newJob.rate_sheet_tier_id > 0) {
      setUseTieredRates(true);
    } else {
      setUseTieredRates(false);
    }
  }, [newJob.rate_sheet_tier_id]);

  useEffect(() => {
    if (newJob.use_propconnect_rates) {
      setUsePcRates(true);
    } else {
      setUsePcRates(false);
    }
  }, [newJob.use_propconnect_rates]);

  useEffect(() => {
    if (projectId) {
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);
      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };
      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/project/${projectId}`,
        requestOptions,
      )
        .then((res) => {
          if (res.status === 401) {
            setAuthStatus(false);
            navigate("/login");
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (data.error) {
            setFormError(data.message);
          } else {
            setNewJob(data.project);
            if (data.project.fsc_area_id !== "" && data.project.rev_fsc_mileage_rate > 0) {
              setUseRevFscMileage(true)
            }
            if (data.project.fsc_area_id !== "" && data.project.cost_fsc_mileage_rate > 0) {
              setUseCostFscMileage(true)
            }
            setOldId(data.project.pc_job_id);
          }
        })
        .catch((err) => {
          setFormError(err);
        });
      return () => controller.abort();
    }
  }, [
    projectId,
    JwtToken,
    setFormError,
    setNewJob,
    navigate,
    setAuthStatus,
    currentCompany,
  ]);

  // get tiered rate sheets depending on customer selection
  useEffect(() => {
    if (newJob.customer_id !== 0 && useTieredRates) {
      setRsOptions([]);
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);

      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/cust-tiered-rate-sheets/${newJob.customer_id}`,
        requestOptions,
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 403) {
              setAuthStatus(false);
              navigate("/login");
            } else {
              toggleNewMessage("Could not get customer tiered rate sheets");
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            return;
          } else {
            let arr = [];
            data.forEach((rs) => {
              if (rs.is_active) {
                arr.push({ value: rs.id, label: rs.rs_name });
              }
            });
            setRsOptions(arr);
          }
        })
        .catch((err) => {
          toggleNewMessage("Something went wrong");
          return;
        });
    }
  }, [
    newJob.customer_id,
    useTieredRates,
    JwtToken,
    navigate,
    setAuthStatus,
    toggleNewMessage,
  ]);

  // get tiers if customer selected
  useEffect(() => {
    if (newJob.customer_rates_id !== 0 && useTieredRates) {
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);

      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/rate-sheet-tiers/${newJob.customer_rates_id}`,
        requestOptions,
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 403) {
              setAuthStatus(false);
              navigate("/login");
            } else {
              toggleNewMessage("Could not get rate sheet tiers");
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            return;
          } else {
            let arr = [];
            data.forEach((t) => {
              arr.push({ value: t.id, label: `Tier ${t.tier_number}` });
            });
            setTierOptions(arr);
          }
        })
        .catch((err) => {
          toggleNewMessage("Something went wrong");
          return;
        });
    }
  }, [
    newJob.customer_rates_id,
    useTieredRates,
    JwtToken,
    navigate,
    toggleNewMessage,
    setTierOptions,
    setAuthStatus,
  ]);

  useEscape(setIsTriggered, [setFormError], [null, null], pending);

  const handleSelect = (option, e) => {
    if (e.name === "customer_fsc_id" && option.value === 0) {
      setUseRevFscMileage(true);
    } else if (e.name === "customer_fsc_id" && option.value > 0) {
      setUseRevFscMileage(false);
    }
    if (e.name === "vendor_fsc_id" && option.value === 0) {
      setUseCostFscMileage(true);
    } else if (e.name === "vendor_fsc_id" && option.value > 0) {
      setUseCostFscMileage(false);
    }
    setNewJob({
      ...newJob,
      [e.name]: option.value,
    });
  };
  const handleFSC = (option) => {
    setNewJob({
      ...newJob,
      fsc_area_id: option.value,
      fsc_area_name: option.label,
    });
  };

  const handleChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;
    setNewJob({
      ...newJob,
      [key]: parseFloat(value),
    });
  };

  useEffect(() => {
    if (usePcRates) {
      setNewJob((current) => ({
        ...current,
        fsc_area_id: null,
        fsc_area_name: null,
        customer_fsc_id: null,
        vendor_fsc_id: null,
        customer_rates_id: 0,
        vendor_rates_id: 0,
        customer_equipment_rate_id: 0,
        customer_empty_boxes_rate_id: 0,
        customer_full_boxes_rate_id: 0,
        vendor_equipment_rate_id: 0,
        vendor_empty_boxes_rate_id: 0,
        vendor_full_boxes_rate_id: 0,
        apply_fsc_to_asset_loads: false,
        fixed_mob_rev: 0,
        fixed_mob_cost: 0,
      }));
    }
  }, [usePcRates]);

  const handleNext = (selectedIndex) => {
    setIndex(selectedIndex);
  };

  const handlePcRates = () => {
    setUsePcRates(!usePcRates);
    setNewJob((currentData) => {
      const newData = currentData;
      newData.use_propconnect_rates = !usePcRates;
      return newData;
    });
    if (!usePcRates === true) {
      setUseTieredRates(false);
    }
  };

  const handleSubmit = () => {
    setPending(true);
    setFormError(null);
    setErrIdx(null);
    if (exists) {
      toggleNewMessage("This project has already been created, please try again.");
      setPending(false);
      return;
    } else if (
      newJob.pc_job_id === "" ||
      newJob.customer_id === 0 ||
      newJob.vendor_id === 0
    ) {
      toggleNewMessage("Please fill out all inputs");
      setErrIdx(0);
      setIndex(0);
      setPending(false);
      return;
    } else if (newJob.customer_id === newJob.vendor_id) {
      toggleNewMessage("Customer cannot be the same as Vendor");
      setErrIdx(0);
      setIndex(0);
      setPending(false);
      return;
    } else if (
      (newJob.customer_rates_id === 0 || newJob.vendor_rates_id === 0) &&
      !usePcRates
    ) {
      toggleNewMessage("Please add rate sheets");
      setErrIdx(2);
      setIndex(2);
      setPending(false);
      return;
    } else if (
      (newJob.fsc_area_id &&
        useCostFscMileage &&
        newJob.cost_fsc_mileage_rate === 0) ||
      (useRevFscMileage && newJob.rev_fsc_mileage_rate === 0) ||
      (!useCostFscMileage && newJob.vendor_fsc_id === 0) ||
      (!useRevFscMileage && newJob.customer_fsc_id === 0)
    ) {
      toggleNewMessage("Please add either an FSC Rate sheet or FSC Mileage Rate");
      setErrIdx(1);
      setIndex(1);
      setPending(false);
      return;
    }
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);

    const reqBody = newJob;
    reqBody.created_by_id = currentUser.id;
    reqBody.updated_by_id = currentUser.id;
    reqBody.use_propconnect_rates = usePcRates;
    if (!useRevFscMileage) {
      reqBody.rev_fsc_mileage_rate = 0;
    }
    if (!useCostFscMileage) {
      reqBody.cost_fsc_mileage_rate = 0;
    }
    var method = "POST";
    if (newJob.id > 0) {
      method = "PATCH";
    }
    const requestOptions = {
      method,
      headers,
      body: JSON.stringify(reqBody),
      signal: controller.signal,
    };
    
    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/project/${newJob.id}`,
      requestOptions,
    )
      .then((res) => {
        if (res.status === 401) {
          setAuthStatus(false);
          navigate("/login");
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (data.error) {
          toggleNewMessage(data.message);
          setPending(false);
          return;
        } else {
          if (method === "PATCH") {
            setProjectLoads(data.data.loads);
            setCurrentProject(data.data.project);
            setProjectTotals(data.data.all_totals);
            setProjectAssetLoads(data.data.asset_loads);
            toggleNewMessage(null, "Project updated");
          } else {
            toggleNewMessage(null, "Project added");
            setProjectData(data.data);
          }
          setIsTriggered(false);
          setPending(false);
        }
      })
      .catch((err) => {
        toggleNewMessage("Something went wrong");
        setPending(false);
      })
      .finally(() => setPending(false));
    return () => controller.abort();
  };

  return isTriggered ? (
    <div className="popup">
      <div className="create-project-popup-inner">
        <Carousel
          activeIndex={index}
          onSelect={handleNext}
          wrap={false}
          variant={"dark"}
          interval={null}
          className="carousel-body"
          style={{ width: "auto", height: "550px" }}
          indicatorLabels={["job-info", "fsc", "rate-sheets"]}
        >
          <Carousel.Item className="item">
            <div className="container-fluid">
              {!pending && (
                <Close
                  setIsTriggered={setIsTriggered}
                  title={"Project Information"}
                />
              )}
              {formError && errIdx === 0 && (
                <p className="error">{formError}</p>
              )}
              <div className="mb-1 row">
                <label
                  className="col-sm-3 form-check-label"
                  htmlFor="use-pc-rates"
                >
                  Use Rates from PropConnect
                  <input
                    id="use-pc-rates"
                    type="checkbox"
                    value={newJob.use_propconnect_rates}
                    checked={newJob.use_propconnect_rates}
                    onChange={handlePcRates}
                    className="form-check-input"
                    style={{ borderColor: "black" }}
                  />
                </label>
                {!usePcRates && (
                  <label
                    className="col-sm-3 form-check-label"
                    htmlFor="use-pc-rates"
                  >
                    Use Tiered Rates
                    <input
                      id="use-tiered-rates"
                      type="checkbox"
                      value={useTieredRates}
                      checked={useTieredRates}
                      onChange={() => setUseTieredRates(!useTieredRates)}
                      className="form-check-input"
                      style={{ borderColor: "black" }}
                    />
                  </label>
                )}
              </div>
              <DropDown
                name={"customer_id"}
                title={"Customer"}
                options={dropDowns.companies}
                onChange={handleSelect}
                className={"dropdown"}
                maxMenuHeight={200}
                required={true}
                value={dropDowns.companies.filter(
                  (x) => x.value === newJob.customer_id,
                )}
              />
              <DropDown
                name={"vendor_id"}
                title={"Vendor"}
                options={dropDowns.companies}
                onChange={handleSelect}
                className={"dropdown"}
                maxMenuHeight={200}
                required={true}
                value={dropDowns.companies.filter(
                  (x) => x.value === newJob.vendor_id,
                )}
              />
              {newJob.id === 0 && (
                <JobsDropdown
                  apiKey={currentCompany.api_key}
                  setNewJob={setNewJob}
                  newJob={newJob}
                />
              )}
              {usePcRates && (
                <FormButton
                  pending={pending}
                  name={"Submit"}
                  onClick={handleSubmit}
                />
              )}
            </div>
          </Carousel.Item>
          {!usePcRates && (
            <Carousel.Item className="item">
              <div className="container-fluid">
                {!pending && (
                  <Close
                    setIsTriggered={setIsTriggered}
                    title={"Fuel Surcharge"}
                  />
                )}
                {!fscPending ? (
                  <FscAreaDropdown
                    setFscArea={handleFSC}
                    id={newJob.fsc_area_id}
                  />
                ) : (
                  <p>Retreiving FSC Areas from EAI...</p>
                )}
                {newJob.fsc_area_id && (
                  <>
                  <DropDown
                    name={"fsc_offset"}
                    title={"Diesel Price Start Day"}
                    options={fscOffsetOptions}
                    onChange={handleSelect}
                    className={"dropdown"}
                    maxMenuHeight={200}
                    required={true}
                    value={fscOffsetOptions.filter(
                      (x) => x.value === newJob.fsc_offset,
                    )}
                  />
                  <DropDown
                    name={"customer_fsc_id"}
                    title={"Customer FSC"}
                    options={dropDowns.fsc}
                    onChange={handleSelect}
                    className={"dropdown"}
                    maxMenuHeight={200}
                    required={true}
                    value={dropDowns.fsc.filter(
                      (x) => x.value === newJob.customer_fsc_id,
                    )}
                  />

                  </>
                )}
                {useRevFscMileage && (
                  <div className="row field">
                    <label className="col-sm-4 col-form-label">
                      Customer FSC $/mile
                    </label>
                    <input
                      name="rev_fsc_mileage_rate"
                      type="number"
                      step={0.01}
                      className="col-auto"
                      required
                      onChange={handleChange}
                      value={newJob.rev_fsc_mileage_rate || ""}
                    />
                  </div>
                )}
                {newJob.fsc_area_id && (
                  <DropDown
                    name={"vendor_fsc_id"}
                    title={"Vendor FSC"}
                    options={dropDowns.fsc}
                    onChange={handleSelect}
                    className={"dropdown"}
                    maxMenuHeight={200}
                    required={true}
                    value={dropDowns.fsc.filter(
                      (x) => x.value === newJob.vendor_fsc_id,
                    )}
                  />
                )}
                {useCostFscMileage && (
                  <div className="row field">
                    <label className="col-sm-4 col-form-label">
                      Vendor FSC $/mile
                    </label>
                    <input
                      name="cost_fsc_mileage_rate"
                      type="number"
                      className="col-auto"
                      step={0.01}
                      required
                      onChange={handleChange}
                      value={newJob.cost_fsc_mileage_rate || ""}
                    />
                  </div>
                )}
              </div>
            </Carousel.Item>
          )}
          {!newJob.use_propconnect_rates && (
            <Carousel.Item className="item">
              <div className="container-fluid">
                {!pending && (
                  <Close
                    setIsTriggered={setIsTriggered}
                    title={"Freight Rate Sheets"}
                  />
                )}
                {formError && errIdx === 2 && (
                  <p className="error">{formError}</p>
                )}
                {!useTieredRates ? (
                  <DropDown
                    name={"customer_rates_id"}
                    title={"Customer Rate"}
                    options={dropDowns.rates}
                    onChange={handleSelect}
                    className={"dropdown"}
                    maxMenuHeight={200}
                    required={true}
                    value={dropDowns.rates.filter(
                      (x) => x.value === newJob.customer_rates_id,
                    )}
                  />
                ) : (
                  <DropDown
                    name={"customer_rates_id"}
                    title={"Customer Rate"}
                    options={rsOptions}
                    onChange={handleSelect}
                    className={"dropdown"}
                    maxMenuHeight={200}
                    required={true}
                    value={
                      rsOptions.filter(
                        (x) => x.value === newJob.customer_rates_id,
                      )[0]
                    }
                  />
                )}
                {useTieredRates && newJob.customer_rates_id !== 0 && (
                  <DropDown
                    name={"rate_sheet_tier_id"}
                    title={"Default Rate Sheet Tier"}
                    options={tierOptions}
                    onChange={handleSelect}
                    className={"dropdown"}
                    maxMenuHeight={200}
                    required={true}
                    value={
                      tierOptions.filter(
                        (x) => x.value === newJob.rate_sheet_tier_id,
                      )[0]
                    }
                  />
                )}
                <DropDown
                  name={"vendor_rates_id"}
                  title={"Vendor Rate"}
                  options={dropDowns.rates}
                  onChange={handleSelect}
                  className={"dropdown"}
                  maxMenuHeight={200}
                  required={true}
                  value={dropDowns.rates.filter(
                    (x) => x.value === newJob.vendor_rates_id,
                  )}
                />
              </div>
            </Carousel.Item>
          )}
          {!usePcRates && (
            <Carousel.Item className="item">
              <div className="container-fluid">
                {!pending && (
                  <Close
                    setIsTriggered={setIsTriggered}
                    title={"Asset Rate Sheets"}
                  />
                )}
                {formError && errIdx === 2 && (
                  <p className="error">{formError}</p>
                )}
                <AssetLoadsCarousel
                  dropdowns={dropDowns}
                  handleFunc={handleSelect}
                  newJob={newJob}
                  setIsFixedCost={setIsFixedCost}
                  setIsFixedRev={setIsFixedRev}
                  isFixedCost={isFixedCost}
                  isFixedRev={isFixedRev}
                  changeFunc={handleChange}
                  setNewJob={setNewJob}
                />
                <FormButton
                  pending={pending}
                  name={"Submit"}
                  onClick={handleSubmit}
                />
              </div>
            </Carousel.Item>
          )}
        </Carousel>
        {formError && <div className="error">{formError}</div>}
      </div>
    </div>
  ) : (
    ""
  );
}
